import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BlogResponseDto } from "../../utils/types/blog";
import blogApi from "../../utils/api/blogApi";
import { logout as logoutService } from "../../services/authService";
import { useAuth } from "../../context/AuthContext";

export default function BlogList() {
  const [blogs, setBlogs] = useState<BlogResponseDto[]>([]);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 6;
  const [loading, setLoading] = useState(false);

useEffect(() => {
  setLoading(true)
  const fetchBlogs = async () => {
    try {
      const { data } = await blogApi.get(`/blogs/admin?page=${page}&pageSize=${pageSize}`);
      setBlogs(data.items);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error(err);
    }
    finally{
      setLoading(false)
    }
  };

  fetchBlogs();
}, [page]);

   const handleAddBlog = () => {
    navigate("/admin/blogs/add"); 
  };

  const handleLogout = () => {
    logoutService();
    logout();
    navigate("/admin/login"); 
  };

  const handleHomepage = () => {
    navigate("/")
  }

  const handleEdit = (id: number) => {
    navigate(`/admin/blogs/edit/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      await blogApi.delete(`/blogs/${id}`);
      setBlogs(blogs.filter((b) => b.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete blog");
    }
  };

  if(loading)
    return (
       <div className="p-6 flex justify-center">
          <h1>Fetching data...</h1>
       </div>
    )
    
    return (
    <div className="p-6">
      {/* Header row with title and buttons */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Admin Blogs</h1>
        <div className="flex gap-2">
          <button
            onClick={handleAddBlog}
            className="bg-green-600 text-white px-4 py-2 rounded hover:opacity-80"
          >
            Add Blog
          </button>
          <button
            onClick={handleHomepage}
            className="bg-orange-600 text-white px-4 py-2 rounded hover:opacity-80"
          >
            Homepage
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-4 py-2 rounded hover:opacity-80"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Blogs table */}
      <table className="w-full table-auto border-collapse border border-gray-300">
        <thead>
          <tr>
            <th className="border border-gray-300 px-4 py-2">ID</th>
            <th className="border border-gray-300 px-4 py-2">Main Image</th>
            <th className="border border-gray-300 px-4 py-2">Title</th>
            <th className="border border-gray-300 px-4 py-2">Excerpt</th>
            <th className="border border-gray-300 px-4 py-2">Published</th>
            <th className="border border-gray-300 px-4 py-2">Location</th>
            <th className="border border-gray-300 px-4 py-2">Actions</th>
          </tr>
        </thead>
       <tbody>
          {blogs.map((b) => (
            <tr key={b.id} className="align-top">
              {/* ID - small width */}
              <td className="border border-gray-300 px-2 py-2 w-12 text-center">{b.id}</td>

              {/* Main Image */}
              <td className="border border-gray-300 px-2 py-2">
                {b.mainImageUrl && (
                  <img
                    src={b.mainImageUrl}
                    alt="main"
                    className="h-32 w-64 object-cover rounded"
                  />
                )}
              </td>

              {/* Title */}
              <td className="border border-gray-300 px-2 py-2 h-32 align-middle">
                <div className="h-full flex items-center">{b.title}</div>
              </td>

              {/* Exceprt */}
              <td className="border border-gray-300 px-2 py-2 h-32 align-middle">
                <div className="h-full flex items-center">{b.excerpt}</div>
              </td>

              {/* Published */}
              <td className="border border-gray-300 px-2 py-2 h-32 align-middle">
                <div className="h-full flex items-center justify-center">
                  {b.isPublished ? "Yes" : "No"}
                </div>
              </td>
              
              {/* Location */}
              <td className="border border-gray-300 px-2 py-2 h-32 align-middle">
                <div className="h-full flex items-center justify-center">
                  {b.localization === 1
                    ? "MK"
                    : b.localization === 2
                    ? "RS"
                    : b.localization === 3
                    ? "EN"
                    : "NotSet"}
                </div>
              </td>

              {/* Actions */}
              <td className="border border-gray-300 px-2 py-2 h-32">
                <div className="h-full flex flex-col justify-center gap-2">
                  <button
                    onClick={() => handleEdit(b.id)}
                    className="bg-blue-600 text-white px-3 py-1 rounded hover:opacity-80"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(b.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:opacity-80"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-center gap-2 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`px-3 py-1 rounded ${p === page ? "bg-blue-600 text-white" : "bg-gray-200"}`}
          >
            {p}
          </button>
        ))}

        <button
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}